<template>
    <div
        class="sticky rounded-lg bottom-0 flex flex-col z-[10000] transition-all"
        :class="{
            'pt-2 pb-4 md:py-3 px-6 w-[calc(100%+2.5rem)] -ml-5 lg:ml-0 lg:w-full bg-white box-shadow sticky-container':
                !isPriceContainerSticky,
            'fixed-container': isPriceContainerSticky,
            '-bottom-full': isDropdownOpen,
            ' lg:bottom-2': !isDropdownOpen,
        }"
    >
        <div
            class="flex sm:items-center flex-wrap sm:flex-nowrap flex-row justify-between w-full sm:mb-0 items-center"
            :class="isPriceContainerSticky && 'mb-2 lg:mb-3 items-start'"
        >
            <div
                class="flex sm:mb-0 items-baseline w-1/3 sm:w-full flex-col"
                :class="!isPriceContainerSticky ? 'mb-0' : 'mb-3'"
            >
                <span class="block text-base sm:text-lg font-Inter-Bold sm:mb-2 md:mb-1 mr-2 sm:mr-0">{{ $t('checkout.price') }}</span>
                <transition name="fade-in-value" mode="out-in">
                    <div
                        :key="$formatCurrency(parseInt(quantity * price))"
                        class="block text-xl font-Inter-Bold"
                        :class="!isPriceContainerSticky ? 'sm:text-[1.5rem]' : 'sm:text-xl'"
                    >
                        {{ $formatCurrency(parseInt(quantity * price)) }}
                    </div>
                </transition>
            </div>
            <div
                class="w-1/3 sm:w-full flex justify-end sm:justify-center md:justify-end -mr-4 md:mr-4 text-center px-2 mb-3 md:mb-0"
                :class="!isPriceContainerSticky ? 'mb-0 flex' : 'md:-mb-8 lg:mb-0'"
            >
                <Input
                    v-model="quantity"
                    is-number-incrementer
                    :placeholder="$t('checkout.product.quantity')"
                    :required="false"
                    class="h-[4rem] rounded-[2px] number-input text-center quantity flex items-center"
                    :class="isPriceContainerSticky ? 'w-[7rem]' : 'w-[6rem]'"
                />
            </div>
            <div class="flex items-center" :class="isPriceContainerSticky && 'md:mb-0 mb-3'">
                <button
                    v-if="$device.isMobile && isPreviewButtonVisible && !isPriceContainerSticky"
                    :class="isPreviewCapturing && 'pointer-events-none opacity-60'"
                    class="h-[3.6rem] w-[3.6rem] hidden bg-black text-center rounded-full text-white z-50 items-center justify-center mr-4"
                    @click="scrollToPreview"
                >
                    <i class="ri-image-line block text-2xl leading-none"></i>
                </button>
                <div class="block transition-all" :class="isPriceContainerSticky && 'w-full sm:w-auto mt-1 lg:mt-0'">
                    <div
                        class="transition font-Inter-Semibold text-center rounded-full text-lg text-black bg-yellow-100 hover:bg-yellow-200 p-0 flex items-center justify-center opacity-100 cursor-pointer"
                        :class="[
                            isPreviewCapturing && 'pointer-events-none opacity-60',
                            !isPriceContainerSticky || $device.isDesktop ? 'h-[3.6rem] w-[3.6rem]' : 'w-14 h-14',
                        ]"
                        @click="handleAddToCartClick"
                    >
                        <i
                            class="text-2xl"
                            :class="
                                isPreviewCapturing || isLoading
                                    ? 'ri-loader-4-line animate-spin block transition leading-none pointer-events-none'
                                    : 'ri-shopping-cart-line'
                            "
                        ></i>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
export default {
    name: 'StickyPriceContainer',
    props: {
        name: {
            type: String,
            required: true,
        },
        productId: {
            type: [String, Number],
            required: true,
        },
        price: {
            type: Number,
            required: true,
        },
        image: {
            type: String,
            required: false,
            default: '',
        },
        slug: {
            type: String,
            required: true,
        },
        options: {
            type: Object,
            required: false,
            default: () => {},
        },
        scrollTargetBottom: {
            validator(value) {
                return value instanceof HTMLElement;
            },
            message: 'The "scrollTargetTop" prop must be an instance of HTMLElement',
        },
        scrollTargetTop: {
            validator(value) {
                return value instanceof HTMLElement;
            },
            message: 'The "scrollTargetTop" prop must be an instance of HTMLElement',
        },
        isPreviewCapturing: {
            type: Boolean,
            required: false,
            default: false,
        },
        isDropdownOpen: {
            type: Boolean,
            required: false,
            default: false,
        },
        extras: {
            type: Object,
            required: false,
            default: () => {},
        },
        /** Optional async callback run before add-to-cart; return image URL to use for the cart item */
        beforeAddToCart: {
            type: Function,
            required: false,
            default: null,
        },
    },
    data() {
        return {
            quantity: 1,
            isPriceContainerSticky: false,
            isPreviewButtonVisible: false,
            customProductId: null,
            isLoading: false,
        };
    },
    mounted() {
        this.handleStickyElements();
        window.addEventListener('scroll', this.handleStickyElements);
        // mobile toolbars change innerHeight and expanding panels move the card without a scroll event
        window.addEventListener('resize', this.handleStickyElements);
        this.resizeObserver = new ResizeObserver(this.handleStickyElements);
        this.resizeObserver.observe(document.body);
        if (this.$route.hash) {
            this.customProductId = this.$route.hash.split('#')[1];
        }
    },
    beforeDestroy() {
        window.removeEventListener('scroll', this.handleStickyElements);
        window.removeEventListener('resize', this.handleStickyElements);
        this.resizeObserver?.disconnect();
    },
    methods: {
        async handleAddToCartClick() {
            if (this.isLoading || this.isPreviewCapturing) return;
            const imageToUse = this.beforeAddToCart ? await this.beforeAddToCart() : this.image;
            await this.addToCart({
                price: { from: this.price },
                brutto_price: { from: this.price },
                image: imageToUse ?? this.image,
                slug: this.slug,
                name: this.name,
                options: this.options,
            });
        },
        async addToCart(payload = {}) {
            const image = payload.image ?? this.image;
            const price = payload.price?.from ?? this.price;
            this.isLoading = true;
            if (this.customProductId) {
                this.updateCartItem(image, price);
            } else {
                try {
                    await this.$axios
                        .$post('/cart-item-add', {
                            product_id: this.productId,
                            quantity: this.quantity,
                            session_id: this.$cookies.get('hsession'),
                            image,
                            price,
                            update: 0,
                            extras: this.extras,
                        })
                        .then((res) => {
                            if (res.success === true) {
                                this.$store.commit('globals/setNotifications', {
                                    title: this.$t('checkout.notifications.added_to_cart_title'),
                                    subtitle: this.$t('checkout.notifications.added_to_cart_subtitle'),
                                    emoji: 'thumbs_up_emoji.png',
                                    productId: this.productId,
                                });
                                this.$axios.get(`/cart/${this.$cookies.get('hsession')}`).then((res) => {
                                    this.$store.commit('checkout/setCartItemsCount', res.data.products.length);
                                });
                            }
                        });
                } catch (err) {
                    console.log(err);
                } finally {
                    this.isLoading = false;
                }
            }
        },
        async updateCartItem(image = this.image, price = this.price) {
            try {
                await this.$axios
                    .$post('/cart-item-update', {
                        id: this.customProductId,
                        quantity: this.quantity,
                        session_id: this.$cookies.get('hsession'),
                        image,
                        price,
                        update: 0,
                        extras: this.extras,
                    })
                    .then((res) => {
                        if (res.success === true) {
                            this.$axios.get(`/cart/${this.$cookies.get('hsession')}`).then((res) => {
                                this.$store.commit('checkout/setCartItemsCount', res.data.products.length);
                            });
                            setTimeout(() => {
                                this.$store.commit('globals/setNotifications', {
                                    title: this.$t('checkout.notifications.updated_title'),
                                    subtitle: this.$t('checkout.notifications.updated_subtitle'),
                                    emoji: 'slightly_smiling_emoji.png',
                                });
                                this.isLoading = false;
                            }, 1000);
                        } else {
                            this.isLoading = false;
                        }
                    });
            } catch (err) {
                console.log(err);
                this.isLoading = false;
            }
        },
        scrollToPreview() {
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        },
        handleStickyElements() {
            const scrollTargetBottom = this.scrollTargetBottom;
            const scrollTargetTop = this.scrollTargetTop;

            if (scrollTargetBottom && scrollTargetBottom instanceof HTMLElement) {
                const bottomElementRect = scrollTargetBottom.getBoundingClientRect();
                this.isPriceContainerSticky = bottomElementRect.bottom <= window.innerHeight;
            }

            if (scrollTargetTop && scrollTargetTop instanceof HTMLElement) {
                const topElementRect = scrollTargetTop.getBoundingClientRect();
                const bottomElementRect = scrollTargetBottom.getBoundingClientRect();
                this.isPreviewButtonVisible = topElementRect.bottom <= window.innerHeight;
                if (topElementRect.bottom <= window.innerHeight && bottomElementRect.bottom <= window.innerHeight) {
                    this.isPreviewButtonVisible = false;
                }
            }
        },
    },
};
</script>

<style scoped>
.fixed-container {
    @keyframes fadeIn {
        from {
            @apply opacity-0;
        }
        to {
            @apply opacity-100;
        }
    }
    @apply opacity-0;
    animation: fadeIn 0.15s forwards;
}
</style>
